/**
 * Notepad Confirm Dialog
 *
 * Simple confirmation dialog for unsaved changes in the notepad editor.
 * Provides Save, Discard, and Cancel options.
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';

interface NotepadConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: () => void;
  onDiscard: () => void;
  onCancel: () => void;
  isSaving?: boolean;
}

export function NotepadConfirmDialog({
  open,
  onOpenChange,
  onSave,
  onDiscard,
  onCancel,
  isSaving = false,
}: NotepadConfirmDialogProps) {
  const { t } = useTranslation('common');

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t('dialog.unsavedChanges.title', 'Unsaved Changes')}</AlertDialogTitle>
          <AlertDialogDescription>
            {t(
              'dialog.unsavedChanges.description',
              'You have unsaved changes. What would you like to do?'
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="flex-col sm:flex-row gap-2">
          <AlertDialogCancel onClick={onCancel} disabled={isSaving}>
            {t('actions.cancel', 'Cancel')}
          </AlertDialogCancel>
          <Button variant="destructive" onClick={onDiscard} disabled={isSaving}>
            {t('dialog.unsavedChanges.discard', 'Discard')}
          </Button>
          <AlertDialogAction onClick={onSave} disabled={isSaving}>
            {isSaving ? t('actions.saving', 'Saving...') : t('actions.save', 'Save')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
